import { getProject, updateProject } from './projectService';

interface CharacterRegistryEntry {
  imageUrl: string;
  generatedAt: string;
  promptId: string;
}

interface CharacterRegistry {
  [characterId: string]: CharacterRegistryEntry;
}

/**
 * Update the character registry for a project with a new character image
 */
export const updateProjectCharacterRegistry = async (
  projectId: string, 
  characterId: string, 
  imageUrl: string,
  promptId: string
): Promise<void> => {
  console.log('🔥 characterRegistryService.updateProjectCharacterRegistry called with:', {
    projectId,
    characterId,
    imageUrl,
    promptId
  });
  
  try {
    console.log('🔍 Getting current project data...');
    // Get current project to preserve existing metadata
    const project = await getProject(projectId);
    console.log('📋 Current project:', project);
    
    const existingRegistry: CharacterRegistry = project.metadata?.characterRegistry || {};
    console.log('🗂️ Existing character registry:', existingRegistry);
    
    const newRegistryEntry = {
      imageUrl,
      generatedAt: new Date().toISOString(),
      promptId
    };
    
    const updatedRegistry = {
      ...existingRegistry,
      [characterId]: newRegistryEntry
    };
    
    console.log('🔄 Updated registry that will be saved:', updatedRegistry);
    
    // Update with new character image
    console.log('📤 Calling updateProject with new metadata...');
    await updateProject(projectId, {
      metadata: {
        ...project.metadata,
        characterRegistry: updatedRegistry
      }
    });
    
    console.log(`✅ Updated character registry for ${characterId} in project ${projectId}`);
  } catch (error) {
    console.error(`❌ Failed to update character registry:`, error);
    throw error;
  }
};

/**
 * Get the character registry for a project
 */
export const getProjectCharacterRegistry = async (projectId: string): Promise<CharacterRegistry> => {
  try {
    const project = await getProject(projectId);
    return project.metadata?.characterRegistry || {};
  } catch (error) {
    console.error(`❌ Failed to get character registry:`, error);
    return {};
  }
};

/**
 * Get character image URL for a specific character
 */
export const getCharacterImageUrl = async (projectId: string, characterId: string): Promise<string | null> => {
  try {
    const registry = await getProjectCharacterRegistry(projectId);
    return registry[characterId]?.imageUrl || null;
  } catch (error) {
    console.error(`❌ Failed to get character image URL:`, error);
    return null;
  }
};

/**
 * Check if character has an image in the registry
 */
export const hasCharacterImage = async (projectId: string, characterId: string): Promise<boolean> => {
  const imageUrl = await getCharacterImageUrl(projectId, characterId);
  return !!imageUrl;
};

/**
 * Remove a character from the registry
 */
export const removeCharacterFromRegistry = async (projectId: string, characterId: string): Promise<void> => {
  try {
    const project = await getProject(projectId);
    const existingRegistry: CharacterRegistry = project.metadata?.characterRegistry || {};
    
    // Remove the character
    const { [characterId]: removed, ...updatedRegistry } = existingRegistry;
    
    await updateProject(projectId, {
      metadata: {
        ...project.metadata,
        characterRegistry: updatedRegistry
      }
    });
    
    console.log(`✅ Removed ${characterId} from character registry in project ${projectId}`);
  } catch (error) {
    console.error(`❌ Failed to remove character from registry:`, error);
    throw error;
  }
};

/**
 * Get all character IDs that have images in the registry
 */
export const getCharactersWithImages = async (projectId: string): Promise<string[]> => {
  try {
    const registry = await getProjectCharacterRegistry(projectId);
    return Object.keys(registry);
  } catch (error) {
    console.error(`❌ Failed to get characters with images:`, error);
    return [];
  }
};
